import {
    setError,
    setFormValues,
    setIsAtQualification,
    setIsCreateCertificateModalOpen,
    setIsPostCreateCertificateSuccess,
    setSelectedQualification,
} from "../CreateCertificateModalSlice";
import { getBase64 } from "../../../common/utils/fileToBase64";
import { Dispatch } from "redux";
import { ErrorInterface } from "../../../common/interfaces/ErrorInterface";
import { toast } from "react-toastify";

interface FormValues {
    qualification: {
        selectedId: string;
        selectedValue: string;
        inputValue: string;
    };
    employee: {
        selectedId: string;
        selectedValue: string;
        inputValue: string;
    };
    educator: {
        selectedId: string;
        selectedValue: string;
    };
    selectedEducator: string;
    validAt: string;
    expiresAt: string;
}

interface HandleCreateCertificateProps {
    formValues: FormValues;
    file: File | null;
    dispatch: Dispatch;
    postCreateCertificate: (params: { data: Record<string, unknown> }) => {
        unwrap: () => Promise<unknown>;
    };
    isEstablishmentCertificate?: boolean;
    establishmentId?: number;
    isOutsourced?: boolean;
}

const getCertificateFileFields = async (
    file: File | null,
): Promise<Record<string, unknown>> => {
    if (!file?.name) {
        return {};
    }

    const certificate = await getBase64(file);
    if (!certificate) {
        return {};
    }

    return {
        certificate,
        file_name: file.name,
    };
};

const getSharedCertificateFields = (
    formValues: FormValues,
): Record<string, unknown> => ({
    qualification_id: formValues.qualification.selectedId,
    valid_at: formValues.validAt,
    expires_at: formValues.expiresAt || null,
});

const getEstablishmentCertificateData = (
    formValues: FormValues,
    establishmentId: number | undefined,
    isOutsourced: boolean | undefined,
    fileFields: Record<string, unknown>,
): Record<string, unknown> => {
    if (!establishmentId) {
        console.error("Establishment ID is not defined");
    }

    const data: Record<string, unknown> = {
        ...getSharedCertificateFields(formValues),
        establishment_id: establishmentId,
        ...fileFields,
    };

    if (isOutsourced) {
        data.is_outsourced = true;
    }

    return data;
};

const getEmployeeCertificateData = (
    formValues: FormValues,
    fileFields: Record<string, unknown>,
): Record<string, unknown> => ({
    ...getSharedCertificateFields(formValues),
    employee_id: formValues.employee.selectedId,
    educator_id: formValues.educator.selectedId,
    ...fileFields,
});

const getPostCreateCertificateData = async (
    formValues: FormValues,
    file: File | null,
    isEstablishmentCertificate: boolean,
    establishmentId?: number,
    isOutsourced?: boolean,
): Promise<Record<string, unknown>> => {
    const fileFields = await getCertificateFileFields(file);

    if (isEstablishmentCertificate) {
        return getEstablishmentCertificateData(
            formValues,
            establishmentId,
            isOutsourced,
            fileFields,
        );
    }

    return getEmployeeCertificateData(formValues, fileFields);
};

const showCreateCertificateSuccessToast = (
    formValues: FormValues,
    isEstablishmentCertificate: boolean,
): void => {
    if (isEstablishmentCertificate) {
        toast.success(
            "Certificaat is toegevoegd voor " +
                formValues.qualification.selectedValue,
        );
        return;
    }

    toast.success(
        "Certificaat " +
            formValues.qualification.selectedValue +
            " is toegevoegd voor " +
            formValues.employee.selectedValue,
    );
};

export const handleCreateCertificate = async (
    props: HandleCreateCertificateProps,
): Promise<void> => {
    const {
        formValues,
        file,
        dispatch,
        postCreateCertificate,
        isEstablishmentCertificate = false,
        establishmentId,
        isOutsourced,
    } = props;

    const data = await getPostCreateCertificateData(
        formValues,
        file,
        isEstablishmentCertificate,
        establishmentId,
        isOutsourced,
    );

    try {
        await postCreateCertificate({
            data: data,
        }).unwrap();
        dispatch(setFormValues(null));
        dispatch(setError(null));
        dispatch(setIsCreateCertificateModalOpen(false));
        dispatch(setSelectedQualification(null));
        dispatch(setIsAtQualification(false));
        dispatch(setIsPostCreateCertificateSuccess(true));
        showCreateCertificateSuccessToast(
            formValues,
            isEstablishmentCertificate,
        );
    } catch (error) {
        const errorData = error as ErrorInterface;
        dispatch(setError(errorData?.data?.message));
    }
};
